import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    AlertTriangle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
} from 'lucide-react';

import { Calendar } from '@rocco/ui/calendar/components/calendar';
import { cn } from '@rocco/utils/cn';
import { useRootData } from '@modules/root';
import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import { BOOKING_BOOK_API_ROUTE } from '~/routes/remix-api.booking.book';
import { useBookingSchedule } from '~/modules-rocco/booking/hooks/use-booking-schedule';
import {
    ConfirmationPhase,
    confirmationDetailsSchema,
} from '~/modules-rocco/booking/components/ConfirmationPhase';
import { ResultPhase } from '~/modules-rocco/booking/components/ResultPhase';
import { getTimezoneShort } from '~/modules-rocco/booking/utils/calendar';
import { useBookMeetingAccordionOptional } from '~/ui-rocco/pages/project-detail/revamp/hooks/use-book-meeting-accordion';
import type { BookMeetingAvailability } from '~/ui-rocco/pages/project-detail/revamp/hooks/use-book-meeting-availability';

dayjs.extend(utc);
dayjs.extend(timezone);

interface SessionRef {
    ID: number;
}

interface BookMeetingRowContentProps {
    /** All bookable CalendarSessions linked to this item (one-on-one + round-robin). */
    sessions: SessionRef[];
    /**
     * `dark` (default): the dark booking card on the dark project accordion.
     * `light`: ink-on-light variant for the white professional detail accordion.
     */
    theme?: 'dark' | 'light';
    /**
     * Controlled availability. The project page eager-prefetches on page load
     * (via the accordion provider) and passes it in, so the calendar is ready
     * when the row opens. When omitted, the component self-fetches on mount (the
     * professional detail page, which has no provider). See BC-4603.
     */
    availabilityState?: BookMeetingAvailability;
}

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

const firstNameOf = (fullName?: string | null): string =>
    (fullName ?? '').trim().split(/\s+/)[0] || 'the team';

/**
 * Inline "Book a Meeting" experience for the revamped detail pages (the open
 * state of the booking accordion row, used by both project and professional
 * detail). Reuses the existing booking backend — availability route,
 * {@link useBookingSchedule} and the shared confirmation/result phases — but
 * lays the selection step out in the Figma design (consultant tile, "Select
 * Date" calendar, "Select Time" dropdowns, Continue) instead of the popup
 * modal. `theme` switches between the dark project card and the light
 * professional-detail card. See BC-4603.
 */
export const BookMeetingRowContent = ({
    sessions,
    theme = 'dark',
    availabilityState,
}: BookMeetingRowContentProps) => {
    const isDark = theme === 'dark';
    // Controlled: the project page eager-prefetches and passes availability in.
    // Uncontrolled: self-fetch on mount (professional detail page).
    const controlled = availabilityState != null;
    const [internalActiveIndex, setInternalActiveIndex] = useState(0);
    const activeIndex = availabilityState?.activeIndex ?? internalActiveIndex;
    const setActiveIndex =
        availabilityState?.setActiveIndex ?? setInternalActiveIndex;
    const sessionId = sessions[activeIndex]?.ID ?? sessions[0]?.ID;

    const serverTimezone =
        availabilityState?.serverTimezone ?? getBrowserTimezone();
    const availabilityFetcher = useFetcher<BookingAvailabilityApiResponse>();

    // Reset fetcher data and reload when switching sessions.
    const prevSessionIdRef = useRef<number | undefined>(undefined);

    // Self-fetch on mount / session change (uncontrolled only — the controlled
    // project page prefetches upstream).
    useEffect(() => {
        if (controlled) return;
        if (!sessionId) return;
        if (
            prevSessionIdRef.current === sessionId &&
            availabilityFetcher.data
        ) {
            return;
        }
        prevSessionIdRef.current = sessionId;
        // singleTimezone: this card has no timezone selector, so request one
        // timezone's slots (with memberId-only members) instead of the full ~3.6MB
        // grid; the route also shortens the window to 2 weeks for it. See BC-4603.
        availabilityFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(serverTimezone)}&singleTimezone=1`,
        );
        // availabilityFetcher intentionally omitted: its state changes must not
        // re-trigger the load or every in-flight response cancels the previous request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, sessionId, serverTimezone]);

    const availability = controlled
        ? availabilityState.availability
        : availabilityFetcher.data?.availability;
    const loadError = controlled
        ? availabilityState.loadError
        : availabilityFetcher.data?.error;
    const isLoading = controlled
        ? availabilityState.isLoading
        : availabilityFetcher.state !== 'idle' || (!availability && !loadError);

    const booking = useBookingSchedule({
        availability,
        serverTimezone,
        actionPath: BOOKING_BOOK_API_ROUTE,
        enableMemberSelection: true,
    });

    const { user } = useRootData();
    // On the project page the accordion provider collapses this row on close;
    // on the professional page there's no provider, so fall back to a reload.
    const bookMeetingAccordion = useBookMeetingAccordionOptional();
    const closeBookMeeting =
        bookMeetingAccordion?.closeBookMeeting ??
        (() => {
            if (typeof window !== 'undefined') window.location.reload();
        });
    const [clientName, setClientName] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [clientCompany, setClientCompany] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Prefill the confirmation form from the signed-in member, mirroring
    // BookingScheduleControls.
    useEffect(() => {
        if (user.__typename !== 'Me') return;
        setClientName(
            [user.FirstName, user.LastName].filter(Boolean).join(' ').trim(),
        );
        setClientEmail(user.Email ?? '');
        setClientCompany(
            user.Professionals.length > 1
                ? ''
                : (user.Professionals[0]?.Title ?? user.Company ?? ''),
        );
    }, [user]);

    useEffect(() => {
        if (booking.success) setIsConfirming(false);
    }, [booking.success]);

    // Reset the booking UI when the user switches to a different session.
    useEffect(() => {
        setIsConfirming(false);
        setShowValidationErrors(false);
    }, [activeIndex]);

    const validationResult = useMemo(
        () =>
            confirmationDetailsSchema.safeParse({
                clientName,
                clientEmail,
                clientCompany,
            }),
        [clientName, clientEmail, clientCompany],
    );
    const fieldErrors = validationResult.success
        ? {}
        : validationResult.error.flatten().fieldErrors;

    // Slots for the currently selected day, so the time dropdown can map a
    // chosen label back to its timestamp.
    const selectedDaySlots = useMemo(() => {
        if (!booking.selectedDateTimestamp) return [];
        const dayKey = dayjs(booking.selectedDateTimestamp)
            .tz(booking.timezoneName)
            .format('YYYY-MM-DD');
        return (
            booking.freeDays.find((day) => day.date === dayKey)?.freeSlots ?? []
        );
    }, [booking.selectedDateTimestamp, booking.timezoneName, booking.freeDays]);

    // Session-level presentation (BC-4603): the heading is the session title.
    // Identity + photo mirror the standalone /booking page (BookingScheduleControls):
    // one-on-one sessions show the consultant's meeting image and name; team/random
    // sessions show the professional thumbnail and title.
    const sessionTitle = availability?.sessionTitle ?? '';

    const isOneOnOne = booking.meetingType === 'one-on-one';
    const consultant = booking.consultedMember;
    const professional = booking.professional;
    // When a specific round-robin person is chosen, the tile shows them instead
    // of the generic professional fallback.
    const selectedMember = booking.selectedMember;

    const displayName = isOneOnOne
        ? (consultant?.name ?? 'Team member')
        : (selectedMember?.name ??
          consultant?.name ??
          professional?.title ??
          'Our team');
    const displaySubtitle = isOneOnOne
        ? (consultant?.professionalName ?? professional?.title ?? '')
        : (selectedMember?.professionalName ??
          professional?.categories?.[0] ??
          '');
    const displayImage = isOneOnOne
        ? (consultant?.avatarUrl ?? null)
        : (selectedMember?.avatarUrl ??
          professional?.libraryThumb?.thumbImageFile ??
          null);
    const professionalName = professional?.title ?? '';
    const professionalBio = professional?.teaser ?? '';
    const firstName = firstNameOf(displayName);

    // Round-robin sessions turn the heading into a person picker, defaulting to
    // the first team member. The name shown is the chosen member, falling back to
    // the current slot's first available member before the default lands, so the
    // heading always names a real person (BC-4603).
    const isRoundRobinPicker =
        booking.meetingType === 'random' && booking.memberOptions.length > 1;
    const autoAssignedName = useMemo(() => {
        const slot = selectedDaySlots.find(
            (candidate) =>
                candidate.timestamp === booking.selectedSlotTimestamp,
        );
        // Slots carry memberId only (the availability route strips per-slot
        // names); resolve the name from the team roster options.
        const memberId = slot?.availableMembers[0]?.memberId;
        if (memberId == null) return '';
        return (
            booking.memberOptions.find(
                (option) => option.value === String(memberId),
            )?.label ?? ''
        );
    }, [
        selectedDaySlots,
        booking.selectedSlotTimestamp,
        booking.memberOptions,
    ]);
    const headingPersonName =
        booking.selectedMember?.name || autoAssignedName || firstName;

    const timezoneAbbr = getTimezoneShort(
        booking.timezoneName,
        booking.selectedSlotTimestamp,
    );

    // Show a layout-matching skeleton on the initial load and when switching
    // sessions, so the row doesn't collapse to a small spinner and jump. A
    // booking POST revalidates the availability fetcher (flipping isLoading
    // mid-submit), so skip the skeleton once we're in the confirm/submit/result
    // flow — otherwise it would swap out the confirmation UI. (BC-4603)
    const inBookingFlow = isConfirming || booking.success || booking.failure;
    if (isLoading && !inBookingFlow) {
        return <BookMeetingRowLoading isDark={isDark} />;
    }

    if (loadError || !availability) {
        return (
            <div
                className={cn(
                    'flex min-h-[15.6rem] w-full flex-col items-center justify-center gap-[0.96rem] pb-[3.84rem] text-center',
                    isDark ? 'text-[#d4d4d4]' : 'text-[#6b6b6b]',
                )}
            >
                <AlertTriangle
                    className={cn(
                        'h-[2.4rem] w-[2.4rem]',
                        isDark ? 'text-white/60' : 'text-[#9a9a9a]',
                    )}
                />
                <p className="font-sans text-[0.96rem]">
                    {loadError?.message ?? 'Booking is unavailable right now.'}
                </p>
            </div>
        );
    }

    const hasSelectedSlot = Boolean(booking.selectedSlotTimestamp);
    const formattedDate = booking.selectedSlotTimestamp
        ? dayjs(booking.selectedSlotTimestamp)
              .tz(booking.timezoneName)
              .format('D MMMM, YYYY')
        : '';
    const formattedTime = booking.selectedSlotTimestamp
        ? dayjs(booking.selectedSlotTimestamp)
              .tz(booking.timezoneName)
              .format('hh:mm a')
              .toUpperCase()
        : '';

    // Colours track the theme so the summary stays legible on both the dark
    // project card and the light professional confirmation panel.
    const meetingSummary = (
        <div className="w-[25rem] shrink-0 text-center max-md:text-left">
            <p
                className={cn(
                    'text-[0.864rem]',
                    isDark ? 'text-white/60' : 'text-[#6b6b6b]',
                )}
            >
                You're requesting a meeting with {displayName} for:
            </p>
            {hasSelectedSlot ? (
                <p
                    className={cn(
                        'mt-[0.96rem] text-[1.248rem] leading-[1.3] font-medium',
                        isDark ? 'text-white' : 'text-[#141414]',
                    )}
                >
                    {formattedDate}
                    <br />
                    {formattedTime} {timezoneAbbr}
                </p>
            ) : null}
        </div>
    );

    const handleConfirm = () => {
        setShowValidationErrors(true);
        if (booking.bookingDisabled || !validationResult.success) return;
        booking.handleSubmit(validationResult.data);
    };

    return (
        <div
            className={cn(
                isDark
                    ? 'pt-[6rem] pb-[3.84rem] text-white max-md:pb-5'
                    : // Professional accordion is full-width, so present the
                      // fixed-width (photo + 50.5rem card) group as one centred,
                      // boxed panel instead of stretching it — a light grey fill
                      // separates it from the white accordion, with vertical
                      // margin so it isn't cramped against the rows above/below.
                      // Sized to hold the same booking-card columns as the dark
                      // project layout so the calendar gets equal room.
                      'mx-auto my-[1.8rem] max-w-[90rem] rounded-[0.768rem] border border-black/[0.06] bg-[#f6f6f6] p-[2.4rem] text-[#1f1f21] max-md:my-4 max-md:p-[1.2rem]',
            )}
        >
            <div
                className={cn(
                    'flex max-md:flex-col',
                    isDark ? 'gap-[20.56rem]' : 'gap-[2.4rem]',
                )}
            >
                {/* Left — consultant tile */}
                <div className="flex w-[27.3rem] shrink-0 flex-col items-center gap-[0.6rem] max-md:w-full">
                    <div className="h-[32.75rem] w-full overflow-hidden opacity-90 max-md:h-[18rem]">
                        {displayImage ? (
                            <img
                                src={displayImage}
                                alt={sessionTitle || displayName}
                                className="mx-auto aspect-[27/37] h-full object-cover object-bottom"
                            />
                        ) : (
                            <div
                                className={cn(
                                    'flex h-full w-full items-center justify-center text-[2.4rem]',
                                    isDark
                                        ? 'bg-white/5 text-white/40'
                                        : 'bg-black/5 text-black/30',
                                )}
                            >
                                {firstName.charAt(0)}
                            </div>
                        )}
                    </div>
                    <p className="mt-1 text-[1.5rem] font-medium">
                        {displayName}
                    </p>
                    <p
                        className={cn(
                            'text-[1.25rem] leading-[1.3]',
                            isDark ? 'text-white/40' : 'text-[#9a9a9a]',
                        )}
                    >
                        {displaySubtitle}
                    </p>
                    {sessions.length > 1 ? (
                        <SessionDotNavigator
                            count={sessions.length}
                            activeIndex={activeIndex}
                            onChange={setActiveIndex}
                        />
                    ) : null}
                </div>

                {/* Right — professional intro + booking card */}
                <div className="min-w-0 flex-1 pt-20">
                    {professionalName ? (
                        <p className="text-[2.4375rem] leading-[1.3] font-[400]">
                            {professionalName}
                        </p>
                    ) : null}
                    {professionalBio ? (
                        <p
                            className={cn(
                                'mt-[2rem] w-[56rem] text-[1.25rem] leading-[1.3]',
                                isDark ? 'text-white/80' : 'text-[#5a5a5a]',
                            )}
                        >
                            {professionalBio}
                        </p>
                    ) : null}

                    {isRoundRobinPicker ? (
                        <div className="mt-[6.75rem] text-[1.625rem]">
                            <PersonSelectHeading
                                isDark={isDark}
                                personName={headingPersonName}
                                value={
                                    booking.selectedMemberId == null
                                        ? ''
                                        : String(booking.selectedMemberId)
                                }
                                onChange={(value) =>
                                    booking.setSelectedMemberId(Number(value))
                                }
                                options={booking.memberOptions}
                            />
                        </div>
                    ) : (
                        <p className="mt-[6.75rem] text-[1.625rem] leading-[1.3]">
                            {sessionTitle ||
                                `Schedule a meeting with ${firstName}`}
                        </p>
                    )}

                    <div
                        className={cn(
                            'relative mt-[3.75rem] rounded-[0.768rem] p-[2.4rem]',
                            isDark
                                ? 'max-w-[65.75rem] bg-[#202020]'
                                : 'max-w-[60rem] border border-black/[0.06] bg-white',
                        )}
                    >
                        {/* Result overlay (success / failure) */}
                        {booking.success || booking.failure ? (
                            <ResultPhase
                                variant={isDark ? 'dark' : 'light'}
                                success={Boolean(booking.success)}
                                failure={Boolean(booking.failure)}
                                statusMessage={booking.statusMessage}
                                onClose={closeBookMeeting}
                                eventId={booking.bookingEventId}
                                showSpamHint={booking.showSpamHint}
                            />
                        ) : isConfirming ? (
                            <ConfirmationPhase
                                variant={isDark ? 'dark' : 'light'}
                                meetingSummary={meetingSummary}
                                clientName={clientName}
                                clientEmail={clientEmail}
                                clientCompany={clientCompany}
                                onClientNameChange={setClientName}
                                onClientEmailChange={setClientEmail}
                                onClientCompanyChange={setClientCompany}
                                userInputsDisabled={
                                    user.__typename === 'Me' ||
                                    booking.submissionState !== 'idle'
                                }
                                companyInputDisabled={
                                    booking.submissionState !== 'idle'
                                }
                                userEmailLabel="Enter your email address"
                                showValidationErrors={showValidationErrors}
                                fieldErrors={fieldErrors}
                                onBack={() => setIsConfirming(false)}
                                onConfirm={handleConfirm}
                                confirmDisabled={booking.bookingDisabled}
                                isProcessing={
                                    booking.submissionState !== 'idle'
                                }
                                buttonLabel={
                                    booking.submissionState !== 'idle'
                                        ? 'Confirming…'
                                        : 'Confirm'
                                }
                                statusMessage={booking.statusMessage}
                                showStatusMessage={
                                    Boolean(booking.statusMessage) &&
                                    !booking.success
                                }
                                showSpamHint={booking.showSpamHint}
                                processingHint="Confirming with calendar provider…"
                            />
                        ) : (
                            <div className="flex gap-[2.4rem] max-md:flex-col">
                                {/* 1. Select Date */}
                                <div
                                    className={cn(
                                        'w-[25.5rem] pr-[1.5rem] pl-2',
                                        !isDark &&
                                            'border-r border-black/10 max-md:border-r-0 max-md:border-b',
                                    )}
                                >
                                    <p
                                        className={cn(
                                            'mb-[2.2rem] text-[0.94rem] font-medium tracking-[-0.046rem]',
                                            isDark
                                                ? 'text-[#9a9a9a]'
                                                : 'text-[#6b6b6b]',
                                        )}
                                    >
                                        <span className="mx-1">1.</span>{' '}
                                        <span>Select Date</span>
                                    </p>
                                    <Calendar
                                        variant={isDark ? 'dark' : 'light'}
                                        naviVariant="right"
                                        timeZone={booking.timezoneName}
                                        freeDays={booking.freeDays}
                                        selectedTimestamp={
                                            booking.selectedDateTimestamp ??
                                            undefined
                                        }
                                        onDateSelect={
                                            booking.handleCalendarSelect
                                        }
                                    />
                                </div>

                                {/* 2. Select Time */}
                                <div
                                    className={cn(
                                        'flex flex-col',
                                        isDark
                                            ? 'min-w-[28rem] flex-1 shrink-0 px-5 md:pr-13'
                                            : 'flex-1 max-md:w-full',
                                    )}
                                >
                                    <p
                                        className={cn(
                                            'mb-[2rem] text-[0.94rem] font-medium tracking-[-0.046rem]',
                                            isDark
                                                ? 'text-[#9a9a9a]'
                                                : 'text-[#6b6b6b]',
                                        )}
                                    >
                                        <span className="mx-1">2.</span>{' '}
                                        <span>Select Time</span>
                                    </p>

                                    <BookingSelect
                                        theme={theme}
                                        label="Meeting duration"
                                        value={String(booking.selectedDuration)}
                                        disabled={booking.isDurationDisabled}
                                        onChange={(value) =>
                                            booking.setSelectedDuration(
                                                Number(value),
                                            )
                                        }
                                        options={booking.durations.map(
                                            (duration) => ({
                                                value: String(duration),
                                                label: `${duration} minute${duration === 1 ? '' : 's'}`,
                                            }),
                                        )}
                                    />

                                    <div
                                        className={cn(
                                            isDark
                                                ? 'mt-[2.46rem]'
                                                : 'mt-[1.152rem]',
                                        )}
                                    >
                                        <BookingSelect
                                            theme={theme}
                                            label="What time works for you?"
                                            value={
                                                booking.selectedSlotTimestamp
                                                    ? String(
                                                          booking.selectedSlotTimestamp,
                                                      )
                                                    : ''
                                            }
                                            disabled={!selectedDaySlots.length}
                                            placeholder="Select a time"
                                            onChange={(value) =>
                                                booking.handleTimeSelectWithAssignment(
                                                    Number(value),
                                                )
                                            }
                                            options={selectedDaySlots.map(
                                                (slot) => ({
                                                    value: String(
                                                        slot.timestamp,
                                                    ),
                                                    label: slot.label,
                                                }),
                                            )}
                                        />
                                    </div>

                                    {booking.selectedMemberId != null &&
                                    booking.freeDays.length === 0 ? (
                                        <p className="mt-[0.696rem] text-[0.672rem] text-[#e0a3a3]">
                                            No availability for{' '}
                                            {selectedMember?.name ??
                                                'this person'}{' '}
                                            right now — try another person or
                                            duration.
                                        </p>
                                    ) : (
                                        <p
                                            className={cn(
                                                'text-[0.875rem]',
                                                isDark
                                                    ? 'mt-[0.8rem] text-white/50'
                                                    : 'mt-[1.152rem] text-[#9a9a9a]',
                                            )}
                                        >
                                            Time &amp; Date shown in{' '}
                                            {timezoneAbbr || 'your local time'}
                                        </p>
                                    )}

                                    <button
                                        type="button"
                                        disabled={!hasSelectedSlot}
                                        onClick={() => setIsConfirming(true)}
                                        className={cn(
                                            'relative -right-2 mt-16 self-end rounded-full text-[0.768rem] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40 max-md:mt-[1.92rem] max-md:w-full',
                                            isDark
                                                ? 'inline-flex h-13 items-center justify-center bg-white px-[1.73rem] text-base leading-[1.3] font-medium text-black shadow-[0_0.156rem_0.545rem_rgba(0,0,0,0.11)]'
                                                : 'bg-[#1f1f21] px-[1.296rem] py-[0.768rem] text-white',
                                        )}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SkeletonBlock = ({
    isDark,
    className,
}: {
    isDark: boolean;
    className?: string;
}) => (
    <div
        className={cn(
            'animate-pulse rounded',
            isDark ? 'bg-white/10' : 'bg-black/[0.07]',
            className,
        )}
    />
);

/**
 * Loading placeholder for the booking card. Mirrors the loaded layout's
 * structure and key dimensions (consultant tile + booking card chrome) so the
 * row keeps a stable height — shown on the initial load and when switching
 * sessions, instead of a small centred spinner that would collapse the row and
 * jump when the slots land. Theme-aware for the dark (project) and light
 * (professional) cards. See BC-4603.
 */
const BookMeetingRowLoading = ({ isDark }: { isDark: boolean }) => (
    <div
        className={cn(
            isDark
                ? 'pt-[6rem] pb-[3.84rem] text-white max-md:pb-5'
                : 'mx-auto my-[1.8rem] max-w-[90rem] rounded-[0.768rem] border border-black/[0.06] bg-[#f6f6f6] p-[2.4rem] max-md:my-4 max-md:p-[1.2rem]',
        )}
    >
        <div
            className={cn(
                'flex max-md:flex-col',
                isDark ? 'gap-[20.56rem]' : 'gap-[2.4rem]',
            )}
        >
            {/* Left — consultant tile */}
            <div className="flex w-[27.3rem] shrink-0 flex-col items-center gap-[0.6rem] max-md:w-full">
                <SkeletonBlock
                    isDark={isDark}
                    className="h-[32.75rem] w-full rounded-none opacity-90 max-md:h-[18rem]"
                />
                <SkeletonBlock
                    isDark={isDark}
                    className="mt-1 h-[1.5rem] w-[12rem]"
                />
                <SkeletonBlock
                    isDark={isDark}
                    className="h-[1.1rem] w-[8rem]"
                />
            </div>

            {/* Right — intro + booking card */}
            <div className="min-w-0 flex-1">
                <SkeletonBlock
                    isDark={isDark}
                    className="h-[2.4375rem] w-[55%]"
                />
                <SkeletonBlock
                    isDark={isDark}
                    className="mt-[2rem] h-[1.25rem] w-[85%]"
                />
                <SkeletonBlock
                    isDark={isDark}
                    className="mt-[0.6rem] h-[1.25rem] w-[70%]"
                />
                <SkeletonBlock
                    isDark={isDark}
                    className="mt-[6.75rem] h-[1.625rem] w-[22rem]"
                />

                <div
                    className={cn(
                        'mt-[3.75rem] rounded-[0.768rem] p-[2.4rem]',
                        isDark
                            ? 'max-w-[65.75rem] bg-[#202020]'
                            : 'max-w-[60rem] border border-black/[0.06] bg-white',
                    )}
                >
                    <div className="flex gap-[2.4rem] max-md:flex-col">
                        {/* Select Date — calendar grid placeholder */}
                        <div
                            className={cn(
                                'w-[25.5rem] pr-[1.5rem] pl-2',
                                !isDark &&
                                    'border-r border-black/10 max-md:border-r-0 max-md:border-b',
                            )}
                        >
                            <SkeletonBlock
                                isDark={isDark}
                                className="mb-[2.2rem] h-[0.94rem] w-[6rem]"
                            />
                            <div className="grid grid-cols-7 gap-[0.6rem]">
                                {Array.from({ length: 35 }).map((_, index) => (
                                    <SkeletonBlock
                                        key={index}
                                        isDark={isDark}
                                        className="aspect-square w-full"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Select Time — selects + "fetching timeslots" note */}
                        <div
                            className={cn(
                                'flex flex-col',
                                isDark
                                    ? 'min-w-[28rem] flex-1 shrink-0 px-5 md:pr-13'
                                    : 'flex-1 max-md:w-full',
                            )}
                        >
                            <SkeletonBlock
                                isDark={isDark}
                                className="mb-[2rem] h-[0.94rem] w-[6rem]"
                            />
                            <SkeletonBlock
                                isDark={isDark}
                                className="h-[2.112rem] w-full rounded-[0.384rem]"
                            />
                            <SkeletonBlock
                                isDark={isDark}
                                className="mt-[2.46rem] h-[2.112rem] w-full rounded-[0.384rem]"
                            />
                            <p
                                className={cn(
                                    'mt-[0.8rem] text-[0.875rem]',
                                    isDark ? 'text-white/50' : 'text-[#9a9a9a]',
                                )}
                            >
                                Fetching available timeslots…
                            </p>
                            <SkeletonBlock
                                isDark={isDark}
                                className="mt-16 h-13 w-[8rem] self-end rounded-full max-md:w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

interface SessionDotNavigatorProps {
    count: number;
    activeIndex: number;
    onChange: (index: number) => void;
}

/** Dot-based session picker shown below the consultant portrait when a project
 *  links more than one active booking session. Matches the Figma pill design:
 *  prev chevron · dots · next chevron inside a rounded border. */
const SessionDotNavigator = ({
    count,
    activeIndex,
    onChange,
}: SessionDotNavigatorProps) => (
    <div className="mt-10 flex items-center gap-[0.288rem] rounded-[999px] border border-[#c4c4c4] px-[0.384rem] py-[0.576rem]">
        <button
            type="button"
            aria-label="Previous session"
            disabled={activeIndex === 0}
            onClick={() => onChange(activeIndex - 1)}
            className="flex size-5 items-center justify-center text-white/60 transition-opacity disabled:opacity-30"
        >
            <ChevronLeft className="size-5" />
        </button>
        {Array.from({ length: count }).map((_, i) => (
            <button
                key={i}
                type="button"
                aria-label={`Session ${i + 1}`}
                onClick={() => onChange(i)}
                className={`size-2.5 rounded-full transition-colors ${
                    i === activeIndex ? 'bg-white' : 'bg-white/30'
                }`}
            />
        ))}
        <button
            type="button"
            aria-label="Next session"
            disabled={activeIndex === count - 1}
            onClick={() => onChange(activeIndex + 1)}
            className="flex size-5 items-center justify-center text-white/60 transition-opacity disabled:opacity-30"
        >
            <ChevronRight className="size-5" />
        </button>
    </div>
);

interface BookingSelectOption {
    value: string;
    label: string;
}

interface BookingSelectProps {
    label: string;
    value: string;
    options: BookingSelectOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    theme?: 'dark' | 'light';
}

/**
 * Clock-icon pill dropdown (native select). On the dark card the `#f6f6f6` pill
 * pops against the dark; on the light card it goes white with a hairline so it
 * still reads against the `#f6f6f6` card.
 */
const BookingSelect = ({
    label,
    value,
    options,
    onChange,
    disabled = false,
    placeholder,
    theme = 'dark',
}: BookingSelectProps) => {
    const isDark = theme === 'dark';
    return (
        <label className="block">
            <span
                className={cn(
                    'mb-[0.7rem] block text-[1.125rem] font-medium',
                    isDark ? 'text-white' : 'text-[#1f1f21]',
                )}
            >
                {label}
            </span>
            <div
                className={cn(
                    'relative flex h-[2.75rem] items-center rounded-[0.384rem] bg-[#f6f6f6] px-4',
                    !isDark && 'border border-black/[0.06]',
                )}
            >
                <Clock className="pointer-events-none size-[1.25rem] shrink-0 text-[#181818]" />
                <select
                    value={value}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                    className="w-full appearance-none bg-transparent px-[0.3rem] text-base font-medium text-[#181818] focus:outline-none disabled:cursor-not-allowed"
                >
                    {placeholder ? (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    ) : null}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <ChevronDown className="pointer-events-none size-4 shrink-0 text-[#181818]" />
            </div>
        </label>
    );
};

/** Open state + outside-click dismissal shared by the booking dropdowns. */
const useDismissableDropdown = () => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () =>
            document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    return { open, setOpen, containerRef };
};

/** Themed option list for the round-robin person picker (BC-4603 Figma). */
const DropdownMenu = ({
    isDark,
    options,
    value,
    onSelect,
    className,
}: {
    isDark: boolean;
    options: BookingSelectOption[];
    value: string;
    onSelect: (value: string) => void;
    className?: string;
}) => (
    <ul
        role="listbox"
        className={cn(
            'absolute z-20 mt-[0.6rem] max-h-[16rem] overflow-y-auto rounded-[0.5rem] border py-[0.4rem]',
            isDark
                ? 'border-white/10 bg-[#2a2a2a] shadow-[0_0.5rem_1.5rem_rgba(0,0,0,0.45)]'
                : 'border-black/10 bg-white shadow-[0_0.5rem_1.5rem_rgba(0,0,0,0.15)]',
            className,
        )}
    >
        {options.map((option) => (
            <li key={option.value}>
                <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => onSelect(option.value)}
                    className={cn(
                        'flex w-full px-[1.2rem] py-[0.6rem] text-left text-base leading-[1.4] transition-colors',
                        isDark
                            ? 'text-white hover:bg-white/10'
                            : 'text-[#1f1f21] hover:bg-black/5',
                        option.value === value && 'font-medium',
                    )}
                >
                    {option.label}
                </button>
            </li>
        ))}
    </ul>
);

/**
 * Round-robin person picker rendered as the booking heading (BC-4603 Figma):
 * "Schedule a meeting with {name}" with a chevron that opens a menu of the
 * session's team members. Defaults to the first member; the chosen person
 * filters the calendar/time availability. Right-aligned so the menu opens under
 * the chevron instead of overflowing the heading.
 */
const PersonSelectHeading = ({
    isDark,
    personName,
    value,
    options,
    onChange,
}: {
    isDark: boolean;
    personName: string;
    value: string;
    options: BookingSelectOption[];
    onChange: (value: string) => void;
}) => {
    const { open, setOpen, containerRef } = useDismissableDropdown();

    return (
        <div ref={containerRef} className="relative inline-block">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className={cn(
                    'flex items-center gap-[0.6rem] text-[1.625rem] leading-[1.3]',
                    isDark ? 'text-white' : 'text-[#1f1f21]',
                )}
            >
                Schedule a meeting with {personName}
                <ChevronDown
                    className={cn(
                        'size-6 shrink-0 transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </button>
            {open ? (
                <DropdownMenu
                    isDark={isDark}
                    options={options}
                    value={value}
                    className="right-0 min-w-[18rem]"
                    onSelect={(next) => {
                        onChange(next);
                        setOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
};
